import { Dimmer, Placeholder, Segment } from '@mairistem/composants';
import { Fiche } from '@mairistem/fiche';
import dayjs from 'dayjs';
import _ from 'lodash';
import React from 'react';
import {
  TileChiffresJourContainer,
  TileChiffresMoisContainer,
  TileEcheancesContainer,
  TileEncaisseContainer,
} from '@/components/Accueil/v2/containers';
import { RegieContext } from '@/contexts';
import { useAutoRefresh } from '@/hooks';
import { useRafraichirIndicateurs } from '@/hooks/accueil';

import FacturesRecusPanel from './Panel/FacturesRecusPanel';

import '@/components/Accueil/v2/Accueil.less';

const EncaissementFactureFiche = () => {
  /** Sert à trigger un rafraichissement des infos lors de l'encaissement.
   * Repasse à false automatiquement au bout de 1s */
  const [encaissement, setEncaissement] = React.useState(false);
  /** Sert à trigger un rafraichissement des infos lors de l'annulation d'un reçu.
   * Repasse à false automatiquement au bout de 1s */
  const [annulation, setAnnulation] = React.useState(false);

  const { regies, selectedRegie } = React.useContext(RegieContext);

  // Rafraîchissement automatique des indicateurs : à l'entrée sur l'écran, périodiquement tant que
  // l'onglet est visible, et au retour sur l'onglet
  const { rafraichir, pret } = useRafraichirIndicateurs();
  useAutoRefresh(rafraichir, pret);

  const handleEncaissement = React.useCallback(() => {
    setEncaissement(true);
  }, []);

  const handleRecuAnnulation = React.useCallback(() => {
    setAnnulation(true);
  }, []);

  React.useEffect(() => {
    let timeoutEncaissement: NodeJS.Timeout;

    if (encaissement) {
      timeoutEncaissement = setTimeout(() => {
        setEncaissement(false);
      }, 1000);
    }

    return () => {
      clearTimeout(timeoutEncaissement);
    };
  }, [encaissement]);

  React.useEffect(() => {
    let timeoutAnnulation: NodeJS.Timeout;

    if (annulation) {
      timeoutAnnulation = setTimeout(() => {
        setAnnulation(false);
      }, 1000);
    }

    return () => {
      clearTimeout(timeoutAnnulation);
    };
  }, [annulation]);

  const regie = React.useMemo(
    () => _.find(regies, { identifiant: selectedRegie }),
    [selectedRegie, regies],
  );

  const loading = React.useMemo(() => _.isNil(regie), [regie]);

  const placeholder = React.useMemo(
    () => (
      <Placeholder style={{ margin: '1em' }} fluid>
        <Placeholder.Paragraph>
          <Placeholder.Line />
          <Placeholder.Line />
          <Placeholder.Line />
          <Placeholder.Line />
          <Placeholder.Line />
          <Placeholder.Line />
        </Placeholder.Paragraph>
        <Placeholder.Paragraph>
          <Placeholder.Line />
          <Placeholder.Line />
          <Placeholder.Line />
          <Placeholder.Line />
          <Placeholder.Line />
          <Placeholder.Line />
        </Placeholder.Paragraph>
        <Placeholder.Paragraph>
          <Placeholder.Line />
          <Placeholder.Line />
          <Placeholder.Line />
          <Placeholder.Line />
          <Placeholder.Line />
          <Placeholder.Line />
        </Placeholder.Paragraph>
        <Placeholder.Paragraph>
          <Placeholder.Line />
          <Placeholder.Line />
          <Placeholder.Line />
          <Placeholder.Line />
          <Placeholder.Line />
          <Placeholder.Line />
        </Placeholder.Paragraph>
        <Placeholder.Paragraph>
          <Placeholder.Line />
        </Placeholder.Paragraph>
      </Placeholder>
    ),
    [],
  );

  return (
    <Fiche
      header={`${_.upperFirst(regie?.titre || 'Nom de la régie')} - ${_.upperFirst(dayjs().format('dddd DD'))} ${_.upperFirst(dayjs().format('MMMM YYYY'))}`}
      type="regie"
      hideAnchors
    >
      <Segment
        id="encaisse-regie"
        className="fiche-panel"
        style={{ borderRadius: 14, background: 'red' }}
        title="Encaisse de la régie"
      >
        <div className="encaissement-dashboard-tiles">
          <TileEncaisseContainer forceRefresh={encaissement || annulation} />
          <TileEcheancesContainer />
          <TileChiffresJourContainer forceRefresh={encaissement || annulation} />
          <TileChiffresMoisContainer forceRefresh={encaissement || annulation} />
        </div>
      </Segment>
      <Segment id="factures-recus" className="fiche-panel" title="Factures et reçus">
        <Dimmer.Dimmable>
          {loading ? (
            placeholder
          ) : (
            <FacturesRecusPanel
              onEncaissement={handleEncaissement}
              onAnnulation={handleRecuAnnulation}
            />
          )}
          <Dimmer inverted />
        </Dimmer.Dimmable>
      </Segment>
    </Fiche>
  );
};

export default EncaissementFactureFiche;
